import React, { useEffect, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import { AutocompleteSeeker, DatePickerSeeker, disabledBefore, IconSeeker, TextFieldSeeker } from "@ui-seeker/core"

import {
	AppAntSwitch, AppBulkEditAction, AppDialogCancel, AppDialogFooter, AppFormDrawer, AppFormGrid, AppFormSection, AppNewsDialogHeader,
	AppNewsFrame, AppNote, AppPillToggle, AppValueChip, secondaryButtonSx,
} from "@components"
import { NEWS_DIALOG_WIDTH } from "@components/News"
import { useToast } from "@hooks"
import { dsColorNew } from "@palette"
import { fetchNewsAnnouncement, fetchNewsAnnouncements, fetchNewsAudiences, saveNewsAnnouncement } from "../@hooks"
import { DISPLAY_MODES, emptyForm, fromRow, MAX_BODY_BYTES, reachesEveryoneNow, toPayload, validate } from "../@services/news"
import { closeNewsEditor, editingAnnouncement, isNewsEditorOpen, newsAudiences, newsAudiencesFailed } from "../@signals"


// Options of a picker: the enabled ones, plus whatever the announcement already holds even if it was
// disabled since — shown by name and flagged, never dropped and never a raw id
function pickerOptions(items, selected, valueOf, labelOf) {
	return items
		.filter(item => item.enabled || selected.includes(valueOf(item)))
		.map(item => ({ value: valueOf(item), label: item.enabled ? labelOf(item) : `${labelOf(item)} (disabled)` }))
}

function useNewsForm() {
	const [form, setForm] = useState(emptyForm())
	const [errors, setErrors] = useState({})

	const load = row => {
		setForm(row ? fromRow(row) : emptyForm())
		setErrors({})
	}

	const setField = (field, value) => {
		setForm(previous => ({ ...previous, [field]: value }))
		setErrors(({ [field]: _dropped, ...rest }) => rest)
	}

	// The file is read in the browser and sent as text: the API takes JSON, not multipart
	const loadFile = file => new Promise((resolve, reject) => {
		if (!file) return resolve()
		if (file.size > MAX_BODY_BYTES) {
			setErrors(previous => ({ ...previous, body: 'File is over 256 KB' }))
			return reject(new Error('file too large'))
		}
		const reader = new FileReader()
		reader.onload = () => {
			setForm(previous => ({ ...previous, body: String(reader.result ?? ''), fileName: file.name }))
			setErrors(({ body: _dropped, ...rest }) => rest)
			resolve()
		}
		reader.onerror = () => {
			setErrors(previous => ({ ...previous, body: 'Could not read the file' }))
			reject(reader.error)
		}
		reader.readAsText(file)
	})

	const isValid = () => {
		const found = validate(form)
		setErrors(found)
		return !Object.keys(found).length
	}

	return { form, errors, load, setField, loadFile, isValid }
}

export default function NewsManagementEditor() {
	const { form, errors, load, setField, loadFile, isValid } = useNewsForm()
	const { showToast } = useToast()
	const [isSaving, setIsSaving] = useState(false)

	const editing = editingAnnouncement.value
	const isOpen = isNewsEditorOpen.value

	useEffect(() => {
		if (!isOpen) return
		load(editing)
		if (!editing) return
		// the row has everything but the HTML: show it now, fill the body in when it arrives
		fetchNewsAnnouncement(editing.id_announcement)
			.then(detail => setField('body', detail.body ?? ''))
			.catch(() => showToast.error('The announcement body could not be loaded'))
	}, [isOpen, editing?.id_announcement])

	const audiences = newsAudiences.value
	const audiencesFailed = newsAudiencesFailed.value
	const companyOptions = pickerOptions(audiences.companies, form.audience_companies, c => c.company, c => c.company)
	const userOptions = pickerOptions(audiences.users, form.audience_users, u => u.uid, u => u.company ? `${u.username} · ${u.company}` : u.username)
	const everyone = !form.audience_companies.length && !form.audience_users.length

	const handleFile = event => {
		loadFile(event.target.files?.[0]).catch(() => showToast.error('That file could not be loaded'))
		// so picking the same file twice still fires a change
		event.target.value = ''
	}

	const handleSave = () => {
		if (!isValid()) return showToast.error('Check the highlighted fields')
		setIsSaving(true)
		saveNewsAnnouncement(toPayload(form), editing?.id_announcement ?? null)
			.then(() => {
				showToast.success(editing ? 'Announcement updated' : 'Announcement created')
				closeNewsEditor()
			})
			.catch(() => showToast.error('The announcement could not be saved'))
			.finally(() => setIsSaving(false))
	}

	return <AppFormDrawer
		width="100%"
		icon="bullhorn"
		title={editing ? 'Edit announcement' : 'New announcement'}
		subtitle="What it says, who sees it and when"
		isOpen={isOpen}
		controller={closeNewsEditor}
		contentSx={{ maxWidth: 1440 }}
		footer={<>
			<AppDialogCancel onClick={closeNewsEditor}/>
			<AppBulkEditAction
				loading={isSaving}
				// failed audiences: an empty picker would silently mean everyone
				disabled={audiencesFailed}
				startIcon={<IconSeeker icon="floppy-disk"/>}
				onClick={handleSave}
			>
				Save
			</AppBulkEditAction>
		</>}
	>
		<Box sx={{ display: 'grid', gridTemplateColumns: `minmax(0, 1fr) ${NEWS_DIALOG_WIDTH}px`, gap: 3, alignItems: 'start' }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<AppFormSection icon="pen" title="Content">
					<AppFormGrid>
						<Box sx={{ gridColumn: 'span 12' }}>
							<TextFieldSeeker label="Title" value={form.title} error={errors.title} onChange={e => setField('title', e.target.value)}/>
						</Box>
						<Box sx={{ gridColumn: 'span 12', display: 'flex', alignItems: 'center', gap: 1.5 }}>
							{/* the button IS the file input's label: no programmatic click for the browser to refuse */}
							<Button component="label" variant="outlined" startIcon={<IconSeeker icon="upload" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>
								Upload .html
								<input type="file" accept=".html,.htm,text/html" hidden onChange={handleFile}/>
							</Button>
							<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>{form.fileName || 'or paste the HTML below'}</Typography>
						</Box>
						<Box sx={{ gridColumn: 'span 12' }}>
							<TextFieldSeeker
								label="HTML body"
								multiline
								minRows={8}
								maxRows={16}
								value={form.body}
								error={errors.body}
								onChange={e => setField('body', e.target.value)}
								inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
							/>
						</Box>
					</AppFormGrid>
				</AppFormSection>

				<AppFormSection icon="users" title="Audience" action={everyone && <AppValueChip tone="accent">Everyone</AppValueChip>}>
					<AppFormGrid>
						{audiencesFailed && <Box sx={{ gridColumn: 'span 12' }}>
							<AppNote severity="error">
								The companies and users could not be loaded, so the audience cannot be chosen and the
								announcement cannot be saved.{' '}
								<Box component="span" onClick={() => fetchNewsAudiences().then(fetchNewsAnnouncements)} sx={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Try again</Box>
							</AppNote>
						</Box>}
						<Box sx={{ gridColumn: 'span 12' }}>
							<AutocompleteSeeker
								multiple
								filterSelectedOptions={false}
								label="Companies"
								placeholder="Everyone"
								disabled={audiencesFailed}
								options={companyOptions}
								value={form.audience_companies}
								onChange={e => setField('audience_companies', e.target.value || [])}
							/>
						</Box>
						<Box sx={{ gridColumn: 'span 12' }}>
							<AutocompleteSeeker
								multiple
								filterSelectedOptions={false}
								label="Users"
								placeholder="Everyone"
								disabled={audiencesFailed}
								options={userOptions}
								value={form.audience_users}
								onChange={e => setField('audience_users', e.target.value || [])}
							/>
						</Box>
						<Typography sx={{ gridColumn: 'span 12', fontSize: 12, color: dsColorNew.textMuted }}>
							Leave both pickers empty to reach every user of every company.
						</Typography>
					</AppFormGrid>
				</AppFormSection>

				<AppFormSection
					icon="calendar"
					title="Publishing"
					action={<AppAntSwitch
						checked={form.active}
						falseText="Saved but not shown"
						trueText="Published"
						onChange={e => setField('active', e.target.checked)}
					/>}
				>
					<AppFormGrid>
						{reachesEveryoneNow(form) && <Box sx={{ gridColumn: 'span 12' }}>
							<AppNote>Saving publishes it to every user right away.</AppNote>
						</Box>}
						<Box sx={{ gridColumn: 'span 6' }}>
							{/* the picker blocks every day after today by default (built for reports); an announcement
							    is scheduled ahead, and one already live keeps a start in the past */}
							<DatePickerSeeker label="Starts" shouldDisableDate={() => false} value={form.start_at} error={errors.start_at} onChange={date => setField('start_at', date)}/>
						</Box>
						<Box sx={{ gridColumn: 'span 6' }}>
							<DatePickerSeeker
								cleanable
								label="Ends (optional)"
								shouldDisableDate={form.start_at ? disabledBefore(form.start_at) : () => false}
								value={form.end_at}
								error={errors.end_at}
								onChange={date => setField('end_at', date)}
							/>
						</Box>
						<Box sx={{ gridColumn: 'span 12' }}>
							<Typography sx={{ fontSize: 12.5, fontWeight: 500, color: dsColorNew.textMuted, mb: 0.75 }}>How often it shows</Typography>
							<AppPillToggle
								size="small"
								value={form.display_mode}
								options={DISPLAY_MODES.map(({ value, label }) => ({ value, label }))}
								onChange={value => setField('display_mode', value)}
							/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.75 }}>
								{DISPLAY_MODES.find(mode => mode.value === form.display_mode)?.hint}
							</Typography>
						</Box>
					</AppFormGrid>
				</AppFormSection>
			</Box>

			{/* the whole popup, not just its body: what is approved here is what users get */}
			<Box sx={{ position: 'sticky', top: 0 }}>
				<Typography sx={{ fontSize: 12, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>
					Preview
				</Typography>
				<Box sx={{ borderRadius: '16px', overflow: 'hidden', border: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.white }}>
					<AppNewsDialogHeader title={form.title || 'Untitled announcement'} date={form.start_at} onClose={() => {}}/>
					<AppNewsFrame html={form.body} title={form.title || 'Announcement preview'} height="clamp(260px, 50vh, 520px)" fixedHeight/>
					{/* the popup's own footer; the button is inert: it shows the real proportions, it is not there to be clicked */}
					<AppDialogFooter>
						<AppBulkEditAction tabIndex={-1} sx={{ pointerEvents: 'none' }}>Got it</AppBulkEditAction>
					</AppDialogFooter>
				</Box>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1 }}>
					The body runs isolated in a sandboxed frame: it cannot reach the session, the app data or its cookies.
				</Typography>
			</Box>
		</Box>
	</AppFormDrawer>
}
